import { config } from '@cotera/client/config/config';
import type { TenantedClient } from '@cotera/client/app/etc/api/typed-client';

/**
 * Where a dataset's parquet files are served from when the API has to serve
 * them itself.
 *
 * The fallback, not the usual path — see {@link resolveDatasetParquetUrl}.
 *
 * Separate from `DatasetPreviewTable` on purpose: this is the auth concern, and
 * it is the only part of the preview that needs `config`. It is also where the
 * outstanding token fix belongs — the token is baked into the URL at
 * registration time today, and `CoteraDuckDB.registerFileURL` caches by name,
 * so a page that re-materializes after the token expires reads a 401. The
 * intended fix is to resolve the token per request rather than per
 * registration, which changes this function and nothing else.
 *
 * There is no org in the path. The server takes it from the request's own
 * tenanted scope and checks the dataset belongs to it, so a caller has no org
 * to name and the server has no claim to validate.
 */
const datasetArtifactUrl = ({
  datasetId,
  kind,
  artifactId,
  token,
}: {
  datasetId: string;
  kind: 'artifacts' | 'workflow';
  artifactId: string;
  token: string | undefined;
}): string => {
  const url = `${config.apiUrl}/api/v1/resources/datasets/${encodeURIComponent(
    datasetId
  )}/${kind}/${encodeURIComponent(artifactId)}.parquet`;
  return token === undefined
    ? url
    : `${url}?token=${encodeURIComponent(token)}`;
};

/**
 * The URL to hand DuckDB for an artifact's parquet — `artifacts` for the
 * dataset's own, `workflow` for the compacted run overlay the Workflows column
 * joins against.
 *
 * Prefers the signed bucket URL, because what DuckDB does with a URL depends
 * entirely on whether ranges are answered. The bucket answers `bytes=0-0` with
 * a `206`, so DuckDB opens the file on its footer and then reads only the
 * column chunks a query touches; the API answers `200` with the whole object,
 * because ranging through it would make every read pay auth and the artifact
 * lookup, so DuckDB buffers the lot up front. On a small dataset the difference
 * is invisible. On a large one over a real network it is the difference between
 * a preview that appears and one that hangs.
 *
 * Falls back to the API for deployments that cannot sign — development — and
 * for a failed lookup, where serving slowly beats not serving at all.
 */
export const resolveDatasetParquetUrl = async ({
  client,
  datasetId,
  artifactId,
  kind = 'artifacts',
  token,
}: {
  /** Narrowed to the one call this makes, so a test need not build a client. */
  client: {
    resource: {
      dataset: Pick<TenantedClient['resource']['dataset'], 'artifactUrl'>;
    };
  };
  datasetId: string;
  artifactId: string;
  kind?: 'artifacts' | 'workflow';
  token: string | undefined;
}): Promise<string> => {
  const result = await client.resource.dataset.artifactUrl({
    id: datasetId,
    artifactId,
    kind,
  });
  if (result.isOk() && result.value.kind === 'signed') {
    return result.value.url;
  }
  return datasetArtifactUrl({ datasetId, kind, artifactId, token });
};
